import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import {
  TICKET_TYPES,
  type Project,
  type AgentSummary,
  type Category,
  type Contact,
  type Organization,
  type TicketDetail,
  type TicketType,
} from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { FormField } from '../components/FormField.tsx';
import { Select } from '../components/Select.tsx';
import { Textarea } from '../components/Textarea.tsx';

interface CustomFieldDef {
  name: string;
  label: string;
  required: boolean;
  type: 'text' | 'textarea' | 'select';
  options?: string[];
}

const CUSTOM_FIELDS: Record<TicketType, CustomFieldDef[]> = {
  escalation: [
    { name: 'client_name', label: 'End client (optional)', type: 'text', required: false },
    { name: 'urgency', label: 'Urgency (optional)', type: 'select', required: false, options: ['low', 'normal', 'high', 'urgent'] },
    { name: 'steps_taken', label: 'Steps already taken (optional)', type: 'textarea', required: false },
    { name: 'test_machine', label: 'Test machine (optional)', type: 'text', required: false },
    { name: 'password_access', label: 'Password access (optional)', type: 'select', required: false, options: ['yes', 'no', 'partial'] },
  ],
  project: [
    { name: 'client_name', label: 'End client', type: 'text', required: true },
    { name: 'urgency', label: 'Urgency', type: 'select', required: true, options: ['low', 'normal', 'high', 'urgent'] },
    { name: 'time_frame', label: 'Time frame (optional)', type: 'text', required: false },
    { name: 'password_access', label: 'Password access', type: 'select', required: true, options: ['yes', 'no', 'partial'] },
  ],
  quote: [
    { name: 'quoted_hours', label: 'Quoted hours', type: 'text', required: true },
    { name: 'quoted_amount_usd', label: 'Quoted amount (USD)', type: 'text', required: false },
    { name: 'sow_summary', label: 'SOW summary', type: 'textarea', required: true },
    { name: 'qwilr_quote_url', label: 'Qwilr quote URL', type: 'text', required: false },
  ],
  chat: [],
  administrative: [
    { name: 'client_name', label: 'End client', type: 'text', required: true },
    { name: 'urgency', label: 'Urgency', type: 'select', required: true, options: ['low', 'normal', 'high', 'urgent'] },
    { name: 'request_kind', label: 'Request kind', type: 'select', required: true, options: ['nda_copy', 'contract_copy', 'poc_change', 'hours_change'] },
    { name: 'new_poc_name', label: 'New POC name (POC change only)', type: 'text', required: false },
    { name: 'new_poc_email', label: 'New POC email (POC change only)', type: 'text', required: false },
    { name: 'new_monthly_hours', label: 'New monthly hours (hours change only — multiple of 5, > 0)', type: 'text', required: false },
    { name: 'notes', label: 'Notes', type: 'textarea', required: false },
  ],
};

export function TicketCreatePage(): React.ReactElement {
  const navigate = useNavigate();
  // Opened from a project's "New ticket": the client is the project's and
  // the ticket is created inside the project.
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');
  const [project, setProject] = useState<Project | null>(null);

  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [organizationId, setOrganizationId] = useState(searchParams.get('organizationId') ?? '');
  const [type, setType] = useState<TicketType>('escalation');
  const [subject, setSubject] = useState('');
  const [clientTicketNumber, setClientTicketNumber] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [requesterContactId, setRequesterContactId] = useState('');
  const [assignedAgentId, setAssignedAgentId] = useState('');
  const [customFields, setCustomFields] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      apiFetch<{ organizations: Organization[] }>('/api/organizations'),
      apiFetch<{ categories: Category[] }>('/api/categories'),
      apiFetch<{ agents: AgentSummary[] }>('/api/agents'),
    ])
      .then(([o, c, a]) => {
        setOrgs(o.organizations);
        setCategories(c.categories);
        setAgents(a.agents);
      })
      .catch((err: unknown) => setLoadError(err instanceof ApiError ? err.message : 'Failed to load form data.'));
  }, []);

  useEffect(() => {
    if (!projectId) return;
    apiFetch<{ project: Project }>(`/api/projects/${projectId}`)
      .then((r) => {
        setProject(r.project);
        setOrganizationId(r.project.organizationId);
      })
      .catch((err: unknown) => setLoadError(err instanceof ApiError ? err.message : 'Failed to load the project.'));
  }, [projectId]);

  useEffect(() => {
    if (!organizationId) {
      setContacts([]);
      setRequesterContactId('');
      return;
    }
    apiFetch<{ contacts: Contact[] }>(`/api/organizations/${organizationId}/contacts`)
      .then((r) => setContacts(r.contacts))
      .catch(() => setContacts([]));
  }, [organizationId]);

  const fields = useMemo(() => CUSTOM_FIELDS[type], [type]);

  function setField(name: string, value: string) {
    setCustomFields((prev) => ({ ...prev, [name]: value }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    setBusy(true);

    const NUMERIC_FIELDS = new Set(['quoted_hours', 'quoted_amount_usd']);
    const cf: Record<string, unknown> = {};
    for (const f of fields) {
      const raw = customFields[f.name]?.trim() ?? '';
      if (!raw) {
        if (f.required) {
          setSubmitError(`${f.label} is required.`);
          setBusy(false);
          return;
        }
        continue;
      }
      if (NUMERIC_FIELDS.has(f.name)) {
        const num = Number(raw);
        if (!Number.isFinite(num) || num < 0) {
          setSubmitError(`${f.label} must be a number.`);
          setBusy(false);
          return;
        }
        cf[f.name] = num;
      } else {
        cf[f.name] = raw;
      }
    }

    try {
      const res = await apiFetch<{ ticket: TicketDetail }>('/api/tickets', {
        method: 'POST',
        json: {
          organizationId,
          type,
          subject: subject.trim(),
          clientTicketNumber: clientTicketNumber.trim() || null,
          descriptionHtml: description,
          categoryId: categoryId || null,
          requesterContactId: requesterContactId || null,
          assignedAgentId: assignedAgentId || null,
          customFields: cf,
          projectId: project?.id ?? null,
        },
      });
      navigate(`/tickets/${res.ticket.id}`);
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : 'Failed to create ticket.');
    } finally {
      setBusy(false);
    }
  }

  if (loadError) return <p className="text-sm text-[var(--color-danger)]">{loadError}</p>;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <h1 className="text-3xl font-bold text-[var(--color-header)]">New ticket</h1>
      {project && (
        <p className="text-[var(--color-muted)]">
          In project{' '}
          <Link to={`/projects/${project.id}`} className="font-medium text-[var(--color-t3-blue)] hover:underline">
            {project.name}
          </Link>{' '}
          for {project.organizationName}. Time on this ticket counts toward the project.
        </p>
      )}

      <Card>
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Organization"
            required
            value={organizationId}
            onChange={(e) => setOrganizationId(e.target.value)}
            disabled={project !== null}
          >
            <option value="">Select organization…</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </Select>

          <Select label="Type" value={type} onChange={(e) => setType(e.target.value as TicketType)}>
            {TICKET_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>

          <FormField
            label="Subject"
            name="subject"
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="sm:col-span-2"
          />

          <FormField
            label="Client ticket # (optional)"
            name="clientTicketNumber"
            value={clientTicketNumber}
            onChange={(e) => setClientTicketNumber(e.target.value)}
            maxLength={50}
            hint="The client's own reference. Prefixes the subject, e.g. TS10058 - Intune configuration."
            className="sm:col-span-2"
          />

          <Select label="Category" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">No category</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>

          <Select
            label="Requester"
            value={requesterContactId}
            onChange={(e) => setRequesterContactId(e.target.value)}
            disabled={!organizationId}
            hint={!organizationId ? 'Pick an organization first.' : undefined}
          >
            <option value="">No requester</option>
            {contacts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name ? `${c.name} <${c.email}>` : c.email}
              </option>
            ))}
          </Select>

          <Select
            label="Assigned agent"
            value={assignedAgentId}
            onChange={(e) => setAssignedAgentId(e.target.value)}
          >
            <option value="">Unassigned</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>

          <Textarea
            label="Description"
            name="description"
            rows={5}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="sm:col-span-2"
          />

          {fields.length > 0 && (
            <fieldset className="sm:col-span-2 space-y-4 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white p-4">
              <legend className="px-1 text-sm font-semibold text-[var(--color-header)]">{type} details</legend>
              {fields.map((f) =>
                f.type === 'textarea' ? (
                  <Textarea
                    key={f.name}
                    label={f.label}
                    name={f.name}
                    rows={3}
                    required={f.required}
                    value={customFields[f.name] ?? ''}
                    onChange={(e) => setField(f.name, e.target.value)}
                  />
                ) : f.type === 'select' ? (
                  <Select
                    key={f.name}
                    label={f.label}
                    name={f.name}
                    required={f.required}
                    value={customFields[f.name] ?? ''}
                    onChange={(e) => setField(f.name, e.target.value)}
                  >
                    <option value="">Select…</option>
                    {f.options?.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <FormField
                    key={f.name}
                    label={f.label}
                    name={f.name}
                    required={f.required}
                    value={customFields[f.name] ?? ''}
                    onChange={(e) => setField(f.name, e.target.value)}
                  />
                ),
              )}
            </fieldset>
          )}

          {submitError && <p className="text-sm text-[var(--color-danger)] sm:col-span-2">{submitError}</p>}

          <div className="sm:col-span-2 flex gap-3">
            <Button type="submit" disabled={busy || !organizationId || !subject.trim()}>
              {busy ? 'Creating…' : 'Create ticket'}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
